﻿<template>
  <div class="emp-landing overflow-clip" :class="billingClass">

    <!-- ═══════════════ HERO ═══════════════ -->
    <section ref="heroSection" class="relative py-20 lg:py-28">
      <div class="relative mx-auto max-w-[1400px] px-5">
        <div class="grid items-center gap-8 lg:grid-cols-[0.78fr_1.45fr]">
          <div class="max-w-xl">
            <div class="eyebrow">For employers</div>
            <h1 class="hero-title font-serif text-4xl font-bold leading-[1.08] tracking-tight text-white lg:text-[3.4rem]">
              <span
                v-for="(w, i) in headlineWords"
                :key="i"
                class="hero-word"
                :style="{ animationDelay: (i * 0.045) + 's' }"
              >{{ w }}&nbsp;</span>
            </h1>
            <p class="mt-5 text-lg leading-relaxed text-white/65">
              Get access to applicants with evaluated foreign credentials: an internationally-educated talent pool most job boards can't reach.
            </p>
            <div class="mt-8 flex flex-wrap gap-4">
              <Button size="lg" as="a" :href="registerHref" class="h-12 px-7 text-base font-semibold">
                <Icon name="user-plus" :size="18" /> Register as Employer
              </Button>
              <Button size="lg" variant="outline" as="a" :href="postJobHref" class="h-12 border-white/25 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10">
                <Icon name="plus-circle" :size="18" /> Post a job
              </Button>
            </div>
            <div class="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-white/45">
              <span class="jb-when-free flex items-center gap-1.5"><Icon name="check" :size="13" /> Free during early access</span>
              <span class="jb-when-free flex items-center gap-1.5"><Icon name="check" :size="13" /> No card required</span>
              <span class="jb-when-paid flex items-center gap-1.5"><Icon name="check" :size="13" /> One plan, $299/mo</span>
              <span class="jb-when-paid flex items-center gap-1.5"><Icon name="check" :size="13" /> Cancel any time</span>
              <span class="flex items-center gap-1.5"><Icon name="check" :size="13" /> Trusted since 2008</span>
            </div>
          </div>

          <div class="hidden lg:flex lg:justify-end lg:-mr-[5vw]">
            <WorldMapHero />
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ TRUST MARQUEE (hidden for now) ═══════════════
    <section class="border-y border-white/10 py-7">
      <div class="trust-marquee">
        <div class="trust-marquee-track">
          <template v-for="(m, i) in marqueeItems.concat(marqueeItems)" :key="i">
            <span class="trust-wordmark">{{ m }}</span>
            <span class="trust-sep" aria-hidden="true">/</span>
          </template>
        </div>
      </div>
    </section>
    -->


    <!-- ═══════════════ BEAM SPINE (value → faq) ═══════════════ -->
    <TracingBeam>
      <div class="mx-auto max-w-[1400px] px-5 lg:pl-12">

        <!-- VALUE / BENTO -->
        <section class="py-20 lg:py-28">
          <div class="sec-head">
            <span class="sec-idx">01</span>
            <h2 class="sec-title font-serif">What an applicant arrives with</h2>
            <p class="sec-sub">Not another inbox of unverified résumés. A talent pool where many have already been measured against U.S. standards.</p>
          </div>
          <BentoValue />
        </section>

        <!-- HOW IT WORKS -->
        <section class="py-20 lg:py-28 border-t border-[color:var(--lp-border)]">
          <div class="sec-head">
            <span class="sec-idx">02</span>
            <h2 class="sec-title font-serif">From post to applicant</h2>
            <p class="sec-sub">No annual contract, no ATS migration, no back-and-forth on credentials.</p>
          </div>
          <StickyHowItWorks :steps="steps" />
        </section>

        <!-- COMPARISON -->
        <section class="py-20 lg:py-28 border-t border-[color:var(--lp-border)]">
          <div class="sec-head">
            <span class="sec-idx">03</span>
            <h2 class="sec-title font-serif">What generic boards leave to you</h2>
            <p class="sec-sub">Indeed and LinkedIn are built for local hiring. For international candidates, the credential question lands on your desk.</p>
          </div>
          <div class="cmp">
            <div class="cmp-row cmp-row--head">
              <div class="cmp-label"></div>
              <div class="cmp-colname cmp-colname--ours"><span class="cmp-ic"></span>Scholaro&nbsp;Jobs</div>
              <div class="cmp-colname"><span class="cmp-ic"></span>Indeed</div>
              <div class="cmp-colname"><span class="cmp-ic"></span>LinkedIn</div>
            </div>
            <div v-for="row in comparisonRows" :key="row.label" class="cmp-row" data-reveal>
              <div class="cmp-label">{{ row.label }}</div>
              <div class="cmp-cell cmp-cell--ours">
                <span class="cmp-m-label">Scholaro Jobs</span>
                <span class="cmp-v">
                  <span class="cmp-ic"><Icon v-if="row.scholaro === true" name="check" :size="15" /></span>
                  <span class="cmp-txt">{{ row.scholaro === true ? 'Available' : row.scholaro }}</span>
                </span>
              </div>
              <div class="cmp-cell">
                <span class="cmp-m-label">Indeed</span>
                <span class="cmp-v">
                  <span class="cmp-ic"><Icon v-if="row.indeed === true" name="check" :size="15" /></span>
                  <span class="cmp-txt" :class="{ 'cmp-dash': row.indeed === false }">{{ row.indeed === false ? '—' : (row.indeed === true ? '' : row.indeed) }}</span>
                </span>
              </div>
              <div class="cmp-cell">
                <span class="cmp-m-label">LinkedIn</span>
                <span class="cmp-v">
                  <span class="cmp-ic"><Icon v-if="row.linkedin === true" name="check" :size="15" /></span>
                  <span class="cmp-txt" :class="{ 'cmp-dash': row.linkedin === false }">{{ row.linkedin === false ? '—' : (row.linkedin === true ? '' : (row.linkedin === 'partial' ? 'Partial' : row.linkedin)) }}</span>
                </span>
              </div>
            </div>
          </div>
          <p class="mt-5 text-xs text-[color:var(--lp-faint)]">Based on publicly documented features as of {{ currentMonth }}. Competitor offerings may change.</p>
        </section>

        <!-- TIMELINE (hidden for now)
        <section class="py-20 lg:py-28 border-t border-[color:var(--lp-border)]">
          <div class="sec-head">
            <span class="sec-idx">04</span>
            <h2 class="sec-title font-serif">Why the evaluation can be trusted</h2>
            <p class="sec-sub">The credential layer isn't new infrastructure — it's what Scholaro has done since 2008.</p>
          </div>
          <CredibilityTimeline />
        </section>
        -->

        <!-- PRICING -->
        <section class="py-20 lg:py-28 border-t border-[color:var(--lp-border)]">
          <div class="sec-head">
            <span class="sec-idx">04</span>
            <h2 class="sec-title font-serif">Straightforward pricing</h2>
            <p class="sec-sub jb-when-free">One simple plan unlocks everything: post jobs, receive and respond to applicants, search the talent pool, and contact people directly. Free during our early access period.</p>
            <p class="sec-sub jb-when-paid">One simple plan unlocks everything: post jobs, receive and respond to applicants, search the talent pool, and contact people directly.</p>
          </div>
          <div class="pricing-grid">
            <div class="price-card price-card--feature" data-reveal>
              <div class="price-badge jb-when-free">Early access offer</div>
              <div class="price-card-head">
                <h3>Employer plan</h3>
                <div class="price-amt jb-when-free"><s class="price-was">$299/mo</s> Free<span class="price-per"> during early access</span></div>
                <div class="price-amt jb-when-paid">$299<span class="price-per">/mo &middot; 1 job post</span></div>
              </div>
              <ul class="price-feats">
                <li class="jb-when-free"><Icon name="check" :size="15" /> Post jobs &amp; receive applicants</li>
                <li class="jb-when-paid"><Icon name="check" :size="15" /> 1 job post per month, with applicants included</li>
                <li><Icon name="check" :size="15" /> Applicant inbox, messaging &amp; screening</li>
                <li><Icon name="check" :size="15" /> Search the talent pool</li>
                <li><Icon name="check" :size="15" /> Filter by credential &amp; work authorization</li>
                <li><Icon name="check" :size="15" /> Contact candidates directly</li>
                <li class="jb-when-free"><Icon name="check" :size="15" /> 30 days' notice before billing begins</li>
                <li class="jb-when-paid"><Icon name="check" :size="15" /> Cancel any time</li>
              </ul>
              <a :href="registerHref" class="price-cta jb-when-free">Get started free</a>
              <a :href="registerHref" class="price-cta jb-when-paid">Get started</a>
            </div>
          </div>
          <p class="mx-auto mt-5 max-w-[1225px] text-xs text-[color:var(--lp-faint)]">No placement fees, no per-click charges, no annual commitment.</p>
        </section>

        <!-- FAQ -->
        <section class="py-20 lg:py-28 border-t border-[color:var(--lp-border)]">
          <div class="sec-head">
            <span class="sec-idx">05</span>
            <h2 class="sec-title font-serif">What cross-border hiring leaders ask</h2>
          </div>
          <div class="max-w-3xl space-y-2.5">
            <details v-for="(q, i) in faqs" :key="q.q" class="faq-item" data-reveal :style="{ transitionDelay: (i * 50) + 'ms' }">
              <summary class="faq-summary">
                <span>{{ q.q }}</span>
                <Icon name="chevron-down" :size="18" class="faq-chevron" />
              </summary>
              <div class="faq-body">{{ q.a }}</div>
            </details>
          </div>
        </section>

      </div>
    </TracingBeam>

    <!-- ═══════════════ FINAL CTA ═══════════════ -->
    <section class="relative overflow-hidden border-t border-white/10 py-24">
      <div class="cta-grid" aria-hidden="true"></div>
      <div class="relative mx-auto max-w-[760px] px-5 text-center">
        <h2 class="font-serif text-3xl font-bold text-white lg:text-5xl">Post your first role in under three minutes.</h2>
        <p class="mx-auto mt-4 max-w-md text-base text-white/65 jb-when-free">Start free, no card required. Once your account is approved, listings go live the moment you publish.</p>
        <p class="mx-auto mt-4 max-w-md text-base text-white/65 jb-when-paid">Once your account is approved, listings go live the moment you publish.</p>

        <!-- Pricing strip: scannable price mention. -->
        <div class="mx-auto mt-7 flex max-w-xl flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
          <span><span class="font-semibold text-white">Everything in one plan</span>: post + search + contact.</span>
          <span class="text-white/25" aria-hidden="true">&middot;</span>
          <span class="jb-when-free"><s class="text-white/40">$299/mo</s> <span class="font-semibold text-white">Free during early access</span></span>
          <span class="jb-when-paid"><span class="font-semibold text-white">$299/mo</span></span>
        </div>

        <div class="mt-8 flex flex-wrap justify-center gap-4">
          <Button size="lg" as="a" :href="registerHref" class="h-12 px-8 text-base font-semibold">
            <Icon name="user-plus" :size="18" /> Register as Employer
          </Button>
          <Button size="lg" variant="outline" as="a" :href="postJobHref" class="h-12 border-white/25 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10">
            <Icon name="plus-circle" :size="18" /> Post a job
          </Button>
          <Button v-if="!isAuthenticated" size="lg" variant="outline" as="a" :href="signInHref" class="h-12 border-white/25 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10">
            <Icon name="log-in" :size="18" /> Sign in
          </Button>
        </div>
      </div>
    </section>

    <!-- ═══════════════ STICKY CTA BAR ═══════════════ -->
    <transition name="sticky-slide">
      <div v-if="showSticky" class="sticky-bar fixed inset-x-0 bottom-0 z-50 border-t border-white/10" :style="{ paddingBottom: 'env(safe-area-inset-bottom)' }">
        <div class="mx-auto flex max-w-[1128px] items-center justify-between gap-4 px-5 py-3">
          <div class="hidden text-sm text-white/70 sm:block">
            <span class="font-semibold text-white">Ready to hire internationally?</span>
            <span class="ml-2 text-white/45 jb-when-free">Free during early access &middot; No card required</span>
            <span class="ml-2 text-white/45 jb-when-paid">One plan &middot; $299/mo</span>
          </div>
          <Button size="lg" as="a" :href="postJobHref" class="h-11 px-6 text-sm font-semibold sm:ml-auto">
            <Icon name="plus-circle" :size="16" /> Post a job
          </Button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, ref, computed } from 'vue';
import WorldMapHero from '../components/WorldMapHero.vue';
import TracingBeam from '../components/TracingBeam.vue';
import BentoValue from '../components/BentoValue.vue';
import StickyHowItWorks from '../components/StickyHowItWorks.vue';
import CredibilityTimeline from '../components/CredibilityTimeline.vue';

declare const window: any;

export default defineComponent({
  name: 'EmployerLandingPage',
  components: { WorldMapHero, TracingBeam, BentoValue, StickyHowItWorks, CredibilityTimeline },
  setup() {
    // SSR-safe: this page is prerendered at build time (scripts/prerender.mjs)
    // so /employer paints before JS. Server render = logged-out variant; the
    // client mount re-reads real auth and corrects. '__PATHBASE__' is replaced
    // with Request.PathBase by EmployerLandingPrerender ('' local, '/jobs' deployed).
    const isClient = typeof window !== 'undefined';
    const auth = isClient ? (window.__AUTH_STATUS__ || {}) : {};
    const appBase = (isClient ? (window.__APP_BASE__ || '/') : '__PATHBASE__/').replace(/\/+$/, '');
    const isAuthenticated = !!auth.isAuthenticated;

    const buildLoginHref = (path: string) =>
      '/Login?ReturnUrl=' + encodeURIComponent(appBase + path);

    // Billing state follows the same SSR-safe pattern as auth: it does not exist
    // during the build-time prerender, so the fragment bakes the free-launch
    // variant. EmployerLanding.cshtml wraps that fragment in .jb-billing-live
    // when billing is on, and the jb-when-free / jb-when-paid CSS below swaps the
    // copy with no JS, so the pre-JS paint is already correct. This ref then
    // carries the same state into the Vue-rendered tree after mount.
    const billingLive = ref(isClient ? !!(window as any).__JOBS_BILLING__?.live : false);

    // Deliberately EMPTY during the prerender: the server wrapper supplies the
    // state class for the pre-JS paint, and a stale class baked into the fragment
    // would sit closer to the copy than the wrapper and hide both variants.
    const billingClass = computed(() =>
      !isClient ? '' : (billingLive.value ? 'jb-billing-live' : 'jb-billing-free'));

    const registerHref = isAuthenticated ? appBase + '/employer/enter' : buildLoginHref('/employer/enter');
    const signInHref = isAuthenticated ? appBase + '/employer/enter' : buildLoginHref('/employer/enter');
    const postJobHref = isAuthenticated ? appBase + '/postjob' : buildLoginHref('/postjob');

    const heroSection = ref<HTMLElement | null>(null);
    const showSticky = ref(false);

    const headlineWords = 'Stop passing on great candidates because their degree looks foreign.'.split(' ');

    const marqueeItems = [
      'AICE Aligned', 'AICE Endorsed', 'USCIS Accepted', 'University Registrars',
      'Licensing Boards', 'Immigration Counsel', 'State Agencies', 'Fortune 500 Employers',
    ];

    const steps = [
      { n: '01', icon: 'rocket', title: 'Post your role in 3 minutes',
        body: 'Describe the position, set visa sponsorship expectations, and add optional screening questions. No annual contract.' },
      { n: '02', icon: 'users', title: 'Applicants arrive with context',
          body: "Resumes and screening answers arrive in your inbox as candidates apply. In the talent pool you also see work-authorization answers and, where a Scholaro credential evaluation is on file, its U.S. equivalency." },
      { n: '03', icon: 'mail', title: 'Message and move forward',
        body: 'Reach out to shortlisted candidates directly from the applicant panel. No screening calls just to verify a degree.' },
    ];

    const comparisonRows = [
      { label: 'Access to applicants with evaluated credentials', scholaro: true, indeed: false, linkedin: false },
      { label: 'Work-authorization status surfaced', scholaro: 'Built in', indeed: false, linkedin: 'partial' },
      { label: 'International candidate focus', scholaro: '190+ countries', indeed: 'General', linkedin: 'General' },
      { label: 'Evaluated credentials shown in candidate search', scholaro: true, indeed: false, linkedin: false },
      { label: 'Designed to meet USCIS requirements', scholaro: true, indeed: false, linkedin: false },
      { label: 'Pricing', scholaro: 'One flat monthly plan', indeed: 'Pay-per-click', linkedin: 'Pay-per-click' },
    ];

    // The two pricing answers track the billing switch. Both previously described
    // the superseded two-tier model (posting free forever, only search paid); the
    // shipped gate puts posting AND search behind the one plan.
    const launchClause = computed(() =>
      billingLive.value ? '' : " It's free during our early access period, and we'll give 30 days' notice before billing begins.");

    const faqs = computed(() => [
      { q: 'Do candidates need a separate credential evaluation, or is it included?',
        a: "Neither is required to apply. Many candidates already have a Scholaro credential evaluation, and talent pool search shows the U.S. equivalency it found. If you'd like the full report, the candidate can order one and share it with you directly." },
      { q: "Can we see a candidate's work-authorization status before reaching out?",
        a: "Yes. In the talent pool, candidates can answer whether they are authorized to work in the U.S. and whether they will need visa sponsorship, and you can filter on both. We ask only those two questions, not visa type." },
      { q: 'Can we search for candidates, or only receive applicants?',
        a: "Both. Beyond the people who apply to your roles, you can proactively search the talent pool, filter by work authorization, and contact people directly, as part of the $299/month employer plan. Talent pool search is unlimited, you can start conversations with up to 50 new candidates a day, and the plan includes 1 job post per month." + launchClause.value },
      { q: "What's the real cost? We've seen 'free to post' before.",
        a: "One plan, $299/month, covers 1 job post per month plus the applicant inbox and screening tools, searching the talent pool, and contacting people directly. Candidates can order a full credential report to share with you. No placement fees, no per-click charges, no annual commitment." + launchClause.value },
      { q: 'How quickly does a listing go live?',
        a: 'Posting takes a few minutes. New employer accounts are reviewed first; once yours is approved, listings go live the moment you publish. Candidates appear in your inbox as they apply, with no batch-release delay (or go straight to your own site, if you choose to take applications there).' },
      { q: 'Can we run Scholaro Jobs alongside our existing ATS (applicant tracking system)?',
        a: "Yes. Choose \"Apply on external website\" when you post, and the Apply button sends candidates straight to your careers site or ATS. Or take applications on Scholaro Jobs, export them to CSV at any time and hand off to your existing workflow. You're not locked in." },
    ]);

    const currentMonth = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

    // ── Animation plumbing ───────────────────────────────────────────────
    let heroObserver: IntersectionObserver | null = null;
    let revealObserver: IntersectionObserver | null = null;

    const motionSafe = typeof window !== 'undefined' && window.matchMedia
      ? !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : true;

    function animateCounter(el: HTMLElement) {
      const target = Number(el.getAttribute('data-counter') || '0');
      const format = el.getAttribute('data-format') || '';
      if (!target) return;
      const start = performance.now();
      const duration = 1800;
      function frame(now: number) {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const value = Math.round(target * eased);
        if (format === 'millions') {
          const m = value / 1000000;
          el.textContent = m >= 1 ? `${m.toFixed(m < 2 ? 1 : 0)}M+` : `${(value / 1000).toFixed(0)}K+`;
        } else if (format === 'plus') {
          el.textContent = `${value}+`;
        } else {
          el.textContent = String(value);
        }
        if (t < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    onMounted(() => {
      document.title = 'Hire international talent | Scholaro Jobs for Employers';

      if (heroSection.value && 'IntersectionObserver' in window) {
        heroObserver = new IntersectionObserver((entries) => {
          showSticky.value = !entries[0].isIntersecting;
        }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' });
        heroObserver.observe(heroSection.value);
      }

      if ('IntersectionObserver' in window) {
        revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target as HTMLElement;
            el.classList.add('is-visible');
            el.querySelectorAll<HTMLElement>('[data-counter]').forEach((counter) => {
              if (counter.dataset.counted) return;
              counter.dataset.counted = '1';
              if (motionSafe) {
                animateCounter(counter);
              } else {
                const tt = Number(counter.getAttribute('data-counter') || '0');
                const f = counter.getAttribute('data-format') || '';
                counter.textContent = f === 'millions' ? `${(tt / 1000000).toFixed(0)}M+` : f === 'plus' ? `${tt}+` : String(tt);
              }
            });
            revealObserver!.unobserve(el);
          });
        }, { threshold: 0.15 });
        document.querySelectorAll('[data-reveal]').forEach((el) => revealObserver!.observe(el));
      }
    });

    onBeforeUnmount(() => {
      heroObserver?.disconnect();
      revealObserver?.disconnect();
    });

    return {
      isAuthenticated, billingLive, billingClass, postJobHref, registerHref, signInHref,
      heroSection, showSticky, headlineWords,
      marqueeItems, steps, comparisonRows, faqs, currentMonth,
    };
  },
});
</script>

<!-- Unscoped on purpose: pre-JS, the state class lives on the server-rendered
     wrapper in EmployerLanding.cshtml, which is outside this component's scope.

     !important is load-bearing here. The scoped rules below pick up a [data-v-*]
     attribute, so .price-badge, .price-cta and .price-feats li tie or beat a
     plain descendant selector and also come later in the file, which left them
     showing the wrong billing variant. These two rules only ever hide, so the
     visible variant keeps whatever display its own classes give it. -->
<style>
.jb-billing-free .jb-when-paid { display: none !important; }
.jb-billing-live .jb-when-free { display: none !important; }
</style>

<style scoped>
.emp-landing {
  /* Dark bookends (hero, trust strip, final CTA) sit on this near-black base;
     the editorial body inside the beam flips to a light surface (see below). */
  background: color-mix(in srgb, var(--jb-hero-bg) 56%, #000);
  color: rgb(var(--lp-fg));
  /* Theme tokens — dark defaults; the light body overrides them on :deep(.tb). */
  --lp-fg: 255,255,255;              /* foreground RGB triplet (text + hairlines) */
  --lp-panel: var(--jb-hero-bg);     /* raised card surface */
  --lp-surface: color-mix(in srgb, var(--jb-hero-bg) 56%, #000); /* section base */
  --lp-tint: #fff;                   /* hover mixes toward this */
  --lp-border: rgba(255,255,255,0.08);
  --lp-faint: rgba(255,255,255,0.35);
}

/* ─── Light editorial body (beam-wrapped middle sections) ───────────────────
   Dark bookends, light body: flip the foreground triplet + surfaces to ink /
   cream so bento, how-it-works, comparison, timeline and FAQ read on light. */
.emp-landing :deep(.tb) {
  background: var(--jb-cream);
  color: var(--jb-ink);
  --lp-fg: var(--jb-ink-rgb);
  --lp-panel: #fff;
  --lp-surface: var(--jb-cream);
  --lp-tint: #000;
  --lp-border: var(--jb-border);
  --lp-faint: var(--jb-ink3);
}

/* ─── Editorial section header ──────────────── */
.eyebrow {
  font-family: var(--jb-font-mono, ui-monospace, monospace);
  font-size: 0.75rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(var(--jb-primary-rgb), 1);
  margin-bottom: 18px;
}
.sec-head { max-width: 56ch; margin-bottom: 48px; }
.sec-idx {
  font-family: var(--jb-font-mono, ui-monospace, monospace);
  font-size: 0.8125rem;
  letter-spacing: 0.16em;
  color: rgba(var(--jb-primary-rgb), 1);
}
.sec-title {
  margin-top: 12px;
  font-size: clamp(1.75rem, 3.5vw, 2.75rem);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: rgb(var(--lp-fg));
}
.sec-sub {
  margin-top: 16px;
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(var(--lp-fg), 0.6);
}

/* ─── Hero word-stagger reveal ──────────────── */
.hero-word {
  display: inline-block;
  opacity: 0;
  animation: word-in 0.55s cubic-bezier(.2, .7, .3, 1) forwards;
}
@keyframes word-in {
  from { opacity: 0; transform: translateY(0.4em); }
  to   { opacity: 1; transform: none; }
}

/* ─── Trust marquee ─────────────────────────── */
.trust-marquee {
  mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
  overflow: hidden;
}
.trust-marquee-track {
  display: flex; align-items: center; gap: 1.75rem;
  width: max-content;
  animation: marquee 48s linear infinite;
}
.trust-marquee:hover .trust-marquee-track { animation-play-state: paused; }
.trust-wordmark {
  font-size: 0.8rem; font-weight: 600;
  letter-spacing: 0.16em; text-transform: uppercase;
  color: rgba(var(--lp-fg), 0.42);
  white-space: nowrap;
}
.trust-sep { color: rgba(var(--lp-fg), 0.18); }

/* ─── Comparison table ──────────────────────── */
.cmp { margin-top: 4px; }
.cmp-row {
  display: grid;
  grid-template-columns: 1.7fr 1.15fr 0.9fr 0.9fr;
  gap: 16px;
  align-items: stretch;
  border-bottom: 1px solid rgba(var(--lp-fg), 0.08);
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.cmp-row.is-visible { opacity: 1; transform: none; }
.cmp-row--head { opacity: 1; transform: none; border-bottom-color: rgba(var(--lp-fg), 0.16); }
.cmp-label,
.cmp-cell,
.cmp-colname { display: flex; align-items: center; padding: 16px 0; }
.cmp-row--head .cmp-colname { padding: 11px 0; }
.cmp-colname {
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.07em;
  text-transform: uppercase; color: rgba(var(--lp-fg), 0.45);
}
.cmp-colname--ours { color: rgba(var(--jb-primary-rgb), 1); }
.cmp-label { font-size: 0.95rem; color: rgba(var(--lp-fg), 0.82); }
.cmp-cell { font-size: 0.9rem; color: rgba(var(--lp-fg), 0.55); }
.cmp-v { display: flex; align-items: center; min-width: 0; }
.cmp-ic { width: 15px; flex: none; display: inline-flex; align-items: center; justify-content: flex-start; margin-right: 8px; }
.cmp-cell :deep(svg) { color: rgba(var(--lp-fg), 0.4); flex-shrink: 0; }
.cmp-cell--ours { font-weight: 600; color: rgb(var(--lp-fg)); }
.cmp-cell--ours :deep(svg) { color: rgba(var(--jb-primary-rgb), 1); }
.cmp-dash { color: rgba(var(--lp-fg), 0.28); }
/* Highlighted Scholaro column: tint the ours track, rounded top/bottom. */
.cmp-colname--ours,
.cmp-cell--ours { background: rgba(var(--jb-primary-rgb), 0.05); padding-inline: 16px; }
.cmp-row--head .cmp-colname--ours { border-radius: 10px 10px 0 0; }
.cmp-row:last-child .cmp-cell--ours { border-radius: 0 0 10px 10px; }
.cmp-m-label { display: none; }
@media (max-width: 720px) {
  .cmp-row { grid-template-columns: 1fr; gap: 0; align-items: start; padding-bottom: 10px; }
  .cmp-row--head { display: none; }
  .cmp-label { padding: 14px 0 6px; font-weight: 600; }
  .cmp-cell { padding: 5px 0; justify-content: space-between; }
  .cmp-cell--ours { background: transparent; padding-inline: 0; border-radius: 0; }
  .cmp-m-label { display: inline; font-size: 0.82rem; font-weight: 500; color: rgba(var(--lp-fg), 0.5); }
}

/* ─── Pricing cards ─────────────────────────── */
.pricing-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  align-items: stretch;
  max-width: 460px;
}
@media (max-width: 720px) { .pricing-grid { grid-template-columns: 1fr; } }
.price-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(var(--lp-fg), 0.12);
  border-radius: 16px;
  background: var(--lp-panel);
  padding: 28px 26px;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.5s ease, border-color 0.25s ease;
}
.price-card.is-visible { opacity: 1; transform: none; }
.price-card--feature {
  padding-top: 34px;
  border-color: rgba(var(--jb-primary-rgb), 0.5);
  background:
    linear-gradient(180deg, rgba(var(--jb-primary-rgb), 0.06), rgba(var(--jb-primary-rgb), 0.02)),
    var(--lp-panel);
  box-shadow: 0 10px 34px -14px rgba(var(--jb-primary-rgb), 0.45);
}
.price-badge {
  position: absolute;
  top: -11px;
  left: 26px;
  display: inline-flex;
  align-items: center;
  background: rgba(var(--jb-primary-rgb), 1);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 5px 11px;
  border-radius: 100px;
  box-shadow: 0 4px 12px -3px rgba(var(--jb-primary-rgb), 0.5);
}
.price-card-head h3 {
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(var(--lp-fg), 0.55);
}
.price-amt {
  margin-top: 12px;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: rgb(var(--lp-fg));
}
.price-was {
  color: rgba(var(--lp-fg), 0.38);
  font-size: 1rem;
  font-weight: 600;
  margin-right: 8px;
}
.price-per {
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(var(--lp-fg), 0.5);
}
.price-feats {
  flex: 1 1 auto;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.price-feats li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 0.875rem;
  line-height: 1.45;
  color: rgba(var(--lp-fg), 0.78);
}
.price-feats :deep(svg) {
  color: rgba(var(--jb-primary-rgb), 1);
  flex-shrink: 0;
  margin-top: 2px;
}
.price-cta {
  margin-top: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: 10px;
  background: rgba(var(--jb-primary-rgb), 1);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 600;
  transition: background 0.15s ease, transform 0.12s ease;
}
.price-cta:hover { background: var(--jb-primary-dark); }
.price-cta:active { transform: scale(0.985); }

/* ─── FAQ (dark) ────────────────────────────── */
.faq-item {
  border: 1px solid rgba(var(--lp-fg), 0.1);
  border-radius: 14px;
  padding: 0 20px;
  background: var(--lp-panel, rgba(var(--lp-fg), 0.02));
  transition: opacity 0.5s ease, transform 0.5s ease, border-color 0.25s ease;
  opacity: 0;
  transform: translateY(10px);
}
.faq-item.is-visible { opacity: 1; transform: none; }
.faq-item:hover { border-color: rgba(var(--jb-primary-rgb), 0.5); }
.faq-summary {
  display: flex; cursor: pointer; list-style: none;
  align-items: center; justify-content: space-between; gap: 16px;
  padding: 18px 0; font-weight: 600; color: rgb(var(--lp-fg));
}
.faq-summary::-webkit-details-marker { display: none; }
.faq-chevron { flex-shrink: 0; color: rgba(var(--jb-primary-rgb), 1); transition: transform 0.3s ease; }
.faq-item[open] .faq-chevron { transform: rotate(180deg); }
.faq-body {
  padding: 0 0 20px; font-size: 0.9rem; line-height: 1.65;
  color: rgba(var(--lp-fg), 0.6); max-width: 64ch;
  animation: faq-reveal 0.35s ease-out;
}
@keyframes faq-reveal { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }

/* ─── Final CTA (flat dotted grid, no glow) ─── */
.cta-grid {
  position: absolute; inset: 0;
  background-image: radial-gradient(rgba(var(--lp-fg), 0.07) 1px, transparent 1.4px);
  background-size: 22px 22px;
  -webkit-mask-image: radial-gradient(60% 60% at 50% 50%, #000, transparent 80%);
  mask-image: radial-gradient(60% 60% at 50% 50%, #000, transparent 80%);
  pointer-events: none;
}

/* ─── Sticky bar ────────────────────────────── */
.sticky-bar { background: color-mix(in srgb, var(--jb-hero-bg) 92%, transparent); backdrop-filter: blur(8px); }
.sticky-slide-enter-from, .sticky-slide-leave-to { transform: translateY(100%); opacity: 0; }
.sticky-slide-enter-active { transition: transform 0.4s cubic-bezier(.34, 1.4, .64, 1), opacity 0.3s ease-out; }
.sticky-slide-leave-active { transition: transform 0.3s ease-in, opacity 0.2s ease-in; }

@keyframes marquee {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

@media (prefers-reduced-motion: reduce) {
  .hero-word { opacity: 1; animation: none; transform: none; }
  .trust-marquee-track { animation: none; }
  .cmp-row, .faq-item { opacity: 1; transform: none; }
}
</style>
